"use client";

import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { PageAboutUsSchema } from "@/sanity/pages/PageAboutUsTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { GroqImage } from "@/helpers/Image";
import { getImageProps } from "next/image";
import PerformanceCard from "./PerformanceCard";

export default function Christopher({ data }: { data: PageAboutUsSchema }) {
  const { props: christopherBg } = getImageProps({
    src: data?.christopherBackgroundImage?.src,
    width: 3000,
    height: 1985,
    alt: "Background",
  });

  return (
    <section className="lg:w-[min(229.25rem,100%)] mx-auto w-[95%] pt-[5rem] lg:pt-[13.5rem] pb-[12.75rem] lg:pb-[23.75rem] overflow-hidden relative rounded-xl bg-[#F0F4F2]">
      <div className="container lg:w-[min(129rem,100%)] flex flex-col items-center relative z-10 px-[3rem] lg:p-0 gap-[5.13rem] lg:gap-[12rem]">
        <div className="flex flex-col items-center gap-[4.8rem] lg:gap-[9rem]">
          <div className="flex flex-col items-center text-center max-w-[80rem]">
            {data.christopherChipTitle && (
              <ChipSerif text={data.christopherChipTitle} />
            )}
            {data?.christopherTitle && (
              <PortableText
                value={data.christopherTitle}
                components={customRichTextComponent({
                  block: {
                    h3: ({ children }) => (
                      <h3 className="font-instrument-sans font-[600] text-[var(--prose-heading-color)] text-[3.5rem] lg:text-[4.5rem]">
                        {children}
                      </h3>
                    ),
                    normal: ({ children }) => (
                      <p className="mb-0 leading-[3rem] max-w-[53.875rem]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}
          </div>
          <div className="flex flex-col items-center lg:flex-row gap-[4.75rem] lg:gap-[7rem]">
            {data?.christopherResume && (
              <div className="flex flex-col gap-[4rem]">
                <PortableText
                  value={data.christopherResume}
                  components={customRichTextComponent({
                    block: {
                      normal: ({ children }) => (
                        <p className="mb-0 leading-[3rem] text-[#103037]/70">
                          {children}
                        </p>
                      ),
                    },
                    list: {
                      bullet: ({ children }) => (
                        <ul className="flex flex-col gap-[4rem] text-[#103037]/70">
                          {children}
                        </ul>
                      ),
                      number: ({ children }) => (
                        <ol className="flex flex-col gap-[4rem] text-[#103037]/70">
                          {children}
                        </ol>
                      ),
                    },
                  })}
                />
              </div>
            )}

            {data?.christopherImageResume && (
              <div className="flex-shrink-0 w-full aspect-[450/458] rounded-[2.5rem] lg:w-[56.25rem]">
                <GroqImage
                  className="object-cover"
                  alt="Christopher Image"
                  src={data?.christopherImageResume}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-[6rem]">
          <div className="flex flex-col items-center text-center">
            {data.christopherPerformanceChip && (
              <ChipSerif text={data.christopherPerformanceChip} />
            )}
            {data?.christopherPerformanceTitle && (
              <PortableText
                value={data.christopherPerformanceTitle}
                components={customRichTextComponent({
                  block: {
                    h2: ({ children }) => (
                      <h2 className="mb-0 font-instrument-sans font-[600] text-[var(--prose-heading-color)] text-[3.5rem] lg:text-[6rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="mb-0 leading-[3rem] max-w-[53.875rem]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}
          </div>

          {data?.christopherPerformanceCards &&
            data.christopherPerformanceCards.length > 0 && (
              <div className="flex flex-col w-full gap-[3.25rem]">
                {data.christopherPerformanceCards.map((card, cardIndex) => (
                  <PerformanceCard
                    key={cardIndex}
                    card={card}
                    index={cardIndex}
                  />
                ))}
              </div>
            )}
        </div>

        <div className="flex flex-col items-center gap-[2.38rem] pt-[4rem] lg:pt-[9rem]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="57"
            height="57"
            viewBox="0 0 57 57"
            fill="none"
          >
            <rect
              width="57"
              height="57"
              fill="white"
              fillOpacity="0.01"
              style={{ mixBlendMode: "multiply" }}
            />
            <path
              d="M21.375 26.7188H10.8834C11.2145 24.5142 12.0017 22.4029 13.1948 20.5197C14.3878 18.6365 15.9606 17.0229 17.8125 15.7819L21.0009 13.6444L19.0416 10.6875L15.8531 12.825C13.1693 14.6135 10.9684 17.0369 9.44585 19.88C7.92328 22.7232 7.12608 25.8983 7.125 29.1234V40.9688C7.125 41.9136 7.50033 42.8197 8.16843 43.4878C8.83653 44.1559 9.74267 44.5312 10.6875 44.5312H21.375C22.3198 44.5312 23.226 44.1559 23.8941 43.4878C24.5622 42.8197 24.9375 41.9136 24.9375 40.9688V30.2812C24.9375 29.3364 24.5622 28.4303 23.8941 27.7622C23.226 27.0941 22.3198 26.7188 21.375 26.7188Z"
              fill="url(#paint0_linear_282_27950)"
            />
            <path
              d="M21.375 26.7188H10.8834C11.2145 24.5142 12.0017 22.4029 13.1948 20.5197C14.3878 18.6365 15.9606 17.0229 17.8125 15.7819L21.0009 13.6444L19.0416 10.6875L15.8531 12.825C13.1693 14.6135 10.9684 17.0369 9.44585 19.88C7.92328 22.7232 7.12608 25.8983 7.125 29.1234V40.9688C7.125 41.9136 7.50033 42.8197 8.16843 43.4878C8.83653 44.1559 9.74267 44.5312 10.6875 44.5312H21.375C22.3198 44.5312 23.226 44.1559 23.8941 43.4878C24.5622 42.8197 24.9375 41.9136 24.9375 40.9688V30.2812C24.9375 29.3364 24.5622 28.4303 23.8941 27.7622C23.226 27.0941 22.3198 26.7188 21.375 26.7188Z"
              fill="#103037"
            />
            <path
              d="M46.3125 26.7188H35.8209C36.152 24.5142 36.9392 22.4029 38.1323 20.5197C39.3253 18.6365 40.8981 17.0229 42.75 15.7819L45.9384 13.6444L43.9969 10.6875L40.7906 12.825C38.1068 14.6135 35.9059 17.0369 34.3833 19.88C32.8608 22.7232 32.0636 25.8983 32.0625 29.1234V40.9688C32.0625 41.9136 32.4378 42.8197 33.1059 43.4878C33.774 44.1559 34.6802 44.5312 35.625 44.5312H46.3125C47.2573 44.5312 48.1635 44.1559 48.8316 43.4878C49.4997 42.8197 49.875 41.9136 49.875 40.9688V30.2812C49.875 29.3364 49.4997 28.4303 48.8316 27.7622C48.1635 27.0941 47.2573 26.7188 46.3125 26.7188Z"
              fill="url(#paint1_linear_282_27950)"
            />
            <path
              d="M46.3125 26.7188H35.8209C36.152 24.5142 36.9392 22.4029 38.1323 20.5197C39.3253 18.6365 40.8981 17.0229 42.75 15.7819L45.9384 13.6444L43.9969 10.6875L40.7906 12.825C38.1068 14.6135 35.9059 17.0369 34.3833 19.88C32.8608 22.7232 32.0636 25.8983 32.0625 29.1234V40.9688C32.0625 41.9136 32.4378 42.8197 33.1059 43.4878C33.774 44.1559 34.6802 44.5312 35.625 44.5312H46.3125C47.2573 44.5312 48.1635 44.1559 48.8316 43.4878C49.4997 42.8197 49.875 41.9136 49.875 40.9688V30.2812C49.875 29.3364 49.4997 28.4303 48.8316 27.7622C48.1635 27.0941 47.2573 26.7188 46.3125 26.7188Z"
              fill="#103037"
            />
            <defs>
              <linearGradient
                id="paint0_linear_282_27950"
                x1="29"
                y1="22.1445"
                x2="29"
                y2="109.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#C1A683" />
                <stop offset="1" stopColor="#F9F9FA" stop-opacity="0" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_282_27950"
                x1="29"
                y1="22.1445"
                x2="29"
                y2="109.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#C1A683" />
                <stop offset="1" stopColor="#F9F9FA" stop-opacity="0" />
              </linearGradient>
            </defs>
          </svg>

          <div className="flex flex-col items-center text-center gap-[2.12rem]">
            {data?.christopherQuote && (
              <PortableText
                value={data.christopherQuote}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="mb-0 text-[2rem] leading-[3rem] text-[#1C1F22] max-w-[39rem] lg:text-[3rem] lg:leading-[4.5rem] lg:max-w-[80.75rem]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}

            {data?.christopherSignature && (
              <div className="flex flex-row items-center gap-[.88rem]">
                <div className="h-[1px] w-[2.6875rem] bg-[#1C1F22]"></div>
                <p className="font-allura text-[3.68188rem] mb-0 text-[#1C1F22] leading-[150%] capitalize">
                  {data.christopherSignature}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          opacity: 0.15,
          mixBlendMode: "multiply",
          background: `linear-gradient(187deg, #93BCDB 5.32%, rgba(147, 188, 219, 0.00) 11.51%), linear-gradient(270deg, #93BCDB 0%, rgba(147, 188, 219, 0.00) 100%), url(${christopherBg.src}) lightgray 50% / cover no-repeat`,
        }}
        className="absolute right-0 bottom-0 z-0 w-full h-full"
      ></div>
    </section>
  );
}
